import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { Card, ProgressRing, SectionHeading } from '@yuan-math/ui';
import type { QuestStatus, WeeklyGoal } from '@yuan-math/learning';

/**
 * Today's three quests, and the week they sit in.
 *
 * The copy is careful about one thing: a quest not finished is never described
 * as a failure or a loss. Yesterday is not mentioned, nothing expires "in 4
 * hours", and the empty state is an invitation rather than a warning. A child
 * who misses a day should find the same friendly page tomorrow.
 */
export function DailyQuests({
  quests,
  weekly,
  practiceLink,
  heading = "Today's quests",
}: {
  quests: QuestStatus[];
  weekly: WeeklyGoal;
  /** Where "start practising" should go, when there is a sensible answer. */
  practiceLink?: string;
  heading?: string;
}): JSX.Element | null {
  if (quests.length === 0) return null;

  const done = quests.filter((entry) => entry.complete).length;
  const swept = done === quests.length;

  return (
    <section className="dashboard-section">
      <SectionHeading
        title={heading}
        description="Three small goals, new every day. Nothing is lost by missing one — they simply change tomorrow."
      />
      <div className="quests">
        <ul className="quest-list">
          {quests.map((entry) => (
            <li key={entry.quest.id}>
              <Card className={`quest ${entry.complete ? 'quest--done' : ''}`}>
                <span className="quest__icon" aria-hidden="true">
                  {entry.quest.icon}
                </span>
                <div className="quest__body">
                  <p className="quest__title">
                    {entry.quest.title}
                    {/* Decorative: completion is already in the progress
                        bar's label, and a bare tick with an aria-label on a
                        span is not reliably announced anyway. */}
                    {entry.complete ? (
                      <span className="quest__tick" aria-hidden="true">
                        {' '}
                        ✓
                      </span>
                    ) : null}
                  </p>
                  <p className="quest__description">{entry.quest.description}</p>
                  <div
                    className="quest__track"
                    role="progressbar"
                    aria-valuenow={entry.current}
                    aria-valuemin={0}
                    aria-valuemax={entry.target}
                    aria-label={`${entry.quest.title}: ${entry.current} of ${entry.target}`}
                  >
                    <div className="quest__fill" style={{ width: `${Math.round(entry.fraction * 100)}%` }} />
                  </div>
                </div>
                <span className="quest__count">
                  {entry.current}/{entry.target}
                  <span className="quest__xp">+{entry.quest.xp} XP</span>
                </span>
              </Card>
            </li>
          ))}
        </ul>

        <Card className="week-card">
          <ProgressRing
            value={weekly.fraction}
            label={`${weekly.daysActive}/${weekly.target}`}
            caption={
              weekly.complete
                ? `${weekly.daysActive} days practised in the last seven — goal met`
                : `${weekly.daysActive} of ${weekly.target} days practised this week`
            }
          />
          <p className="week-card__note">
            {weekly.complete
              ? 'Your week is done. Anything else is extra.'
              : `${weekly.target - weekly.daysActive} more ${weekly.target - weekly.daysActive === 1 ? 'day' : 'days'} to reach your weekly goal.`}
          </p>
          <p className="week-card__quests">
            {swept ? 'All three quests done today.' : `${done} of ${quests.length} quests done today.`}
          </p>
          {practiceLink && !swept ? (
            <Link to={practiceLink} className="btn btn--secondary btn--sm">
              Practise now
            </Link>
          ) : null}
        </Card>
      </div>
    </section>
  );
}
